import { Component, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { CalculatorFormStateService } from '../state/calculator-form-state.service';
import { SessionContextService } from '../../../core/services/session-context.service';

@Component({
  selector: 'app-calculator-rail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './calculator-rail.component.html',
  styleUrl: './calculator-rail.component.scss',
})
export class CalculatorRailComponent {
  protected readonly formState = inject(CalculatorFormStateService);
  protected readonly sessionCtx = inject(SessionContextService);

  // ── Scenario inputs — the same signals CalculatorComponent reads ──
  protected readonly clientAge = this.formState.clientAge;
  protected readonly gender = this.formState.gender;
  protected readonly ownership = this.formState.ownership;
  protected readonly spouseAge = this.formState.spouseAge;
  protected readonly deferralYear = this.formState.deferralYear;
  protected readonly paymentType = this.formState.paymentType;
  protected readonly stateCode = this.formState.stateCode;
  protected readonly amountType = this.formState.amountType;
  protected readonly contributionDisplay = this.formState.contributionDisplay;
  protected readonly states = this.formState.states;
  protected readonly ageError = this.formState.ageError;
  protected readonly spouseAgeError = this.formState.spouseAgeError;
  protected readonly deferralError = this.formState.deferralError;
  protected readonly contributionError = this.formState.contributionError;
  protected readonly savedSearches = this.formState.savedSearches;
  protected readonly activeSearchName = this.formState.activeSearchName;
  protected readonly loading = this.formState.loading;
  protected readonly canReset = this.formState.canReset;

  // ── Refine ── narrows rows already returned; no re-query ────────
  protected readonly hasSearched = this.formState.hasSearched;
  protected readonly searchDisplayText = this.formState.searchDisplayText;
  protected readonly amBestFilter = this.formState.amBestFilter;
  protected readonly surrYearFilter = this.formState.surrYearFilter;
  protected readonly benefitBaseFilter = this.formState.benefitBaseFilter;
  protected readonly amBestOptions = this.formState.amBestOptions;
  protected readonly surrYearOptions = this.formState.surrYearOptions;
  protected readonly riderTypeOptions = this.formState.riderTypeOptions;
  protected readonly amBestOpen = signal(false);
  /** "ALL", "A++, A+", or "A++, A+ +4" — the same summary the header chip shows. */
  protected readonly amBestSummary = computed(() => {
    const r = this.amBestFilter();
    if (!r.length) return 'ALL';
    return r.length <= 2 ? r.join(', ') : `${r.slice(0, 2).join(', ')} +${r.length - 2}`;
  });

  // searchDisplayText updates per keystroke so the box feels live; searchText,
  // which the results grid filters on, lags it by 200ms.
  private readonly search$ = new Subject<string>();

  constructor() {
    this.search$.pipe(debounceTime(200), takeUntilDestroyed())
      .subscribe((v) => this.formState.searchText.set(v));
  }

  onSearchInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.formState.searchDisplayText.set(v);
    this.search$.next(v);
  }

  onSearchClear(): void {
    this.formState.searchDisplayText.set('');
    this.formState.searchText.set('');
  }

  toggleAmBest(rating: string): void { this.formState.toggleAmBest(rating); }
  setSurrFilter(val: number | null): void { this.formState.setSurrYearFilter(val); }

  switchToJoint(): void { this.formState.switchToJoint(); }

  // ── Whole-number inputs (Client Age, Spouse Age, Deferral) ──────
  // Keydown stops non-digit keys before they land; the input handler cleans
  // whatever still gets through (paste, drag-drop, autofill) and caps at max.

  /** Let only digits through; editing/navigation keys and shortcuts pass. */
  onWholeNumberKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  /** Digits only, never above `max`: a keystroke that would pass `max` is
   *  dropped (typing 150 into Age stops at 15). Empty becomes null so the
   *  calculator's required check still fires. */
  onWholeNumberInput(event: Event, target: WritableSignal<number>, max: number,
                     error: WritableSignal<string>): void {
    const el = event.target as HTMLInputElement;
    let digits = el.value.replace(/\D/g, '');
    while (digits && Number(digits) > max) digits = digits.slice(0, -1);
    const next = digits === '' ? null : Number(digits);
    const shown = next == null ? '' : String(next);
    if (el.value !== shown) el.value = shown;
    target.set(next as unknown as number);
    error.set('');
  }

  onContributionInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    this.formState.setContributionFromInput(el.value);
    // A dropped over-cap keystroke can leave the display signal unchanged, so
    // the [value] binding won't repaint the input — write it back directly.
    const shown = this.formState.contributionDisplay();
    if (el.value !== shown) el.value = shown;
    this.formState.contributionError.set('');
  }

  onContributionBlur(): void { this.formState.normalizeContributionDisplay(); }

  // ── Actions — CalculatorComponent owns the side effects, we just ask ──
  onCalculate(): void { this.formState.requestCalculate(); }
  onReset(): void { this.formState.requestResetInputs(); }
  onSaveDefaults(): void { this.formState.requestSaveDefaults(); }
  onSavedSearchChange(name: string): void { this.formState.requestSavedSearch(name); }

  protected readonly showSaveSearch = signal(false);
  protected readonly saveSearchName = signal('');

  saveSearch(): void {
    const name = this.saveSearchName().trim();
    if (!name) return;
    this.formState.requestSaveSearch(name);
    this.saveSearchName.set('');
    this.showSaveSearch.set(false);
  }

  // ── My Shelf toggle ──────────────────────────────────────────────

  /** Should the Scope block render at all?
   *
   *  Shown for 'all' and 'd_shelf' (shelfPermissionIA on the signed-in user),
   *  hidden for 'shelf':
   *    'shelf'   → locked to shelf products; no choice, so no control.
   *    'all'     → shown, defaulting to All.
   *    'd_shelf' → shown, defaulting to My Shelf.
   *
   *  The whole rail-scope group goes, not just the switch: unlike the rates rail
   *  this group holds nothing else, so keeping it would leave an orphan "Scope"
   *  heading.
   *
   *  Enforcement is unaffected — isShelfActive('IA') still returns true for
   *  'shelf', so results stay shelf-scoped without the control. The calculator
   *  is IA-only, hence the fixed 'IA'. */
  protected readonly showShelfScope = computed(() =>
    !this.sessionCtx.hasSession() || this.sessionCtx.shouldShowToggle('IA'));

  protected readonly shelfState = computed<'off' | 'on' | 'locked' | 'disabled'>(() => {
    if (!this.sessionCtx.hasSession()) return 'disabled';
    if (this.sessionCtx.shouldForceShelf('IA')) return 'locked';
    return this.sessionCtx.shelfMode() ? 'on' : 'off';
  });

  onShelfClick(): void {
    const s = this.shelfState();
    if (s === 'locked' || s === 'disabled') return;
    this.sessionCtx.toggleShelfMode();
  }
}
